import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, X, MessageSquare, PanelLeftClose } from 'lucide-react';
import { useSessions } from '../../hooks/useSessions';
import { useChatStore } from '../../store/chatStore';
import { useUIStore } from '../../store/uiStore';
import { addToast } from '../../store/notificationStore';
import { chatService } from '../../services/chatService';
import { getSessionGroupLabel, SESSION_GROUP_ORDER } from '../../utils/formatDate';
import { cn } from '../../utils/cn';
import type { ChatSession, SessionGroup } from '../../types';

/**
 * Sidebar — conversation management panel.
 *
 * Renders "New Chat" button, debounced search input, session list
 * grouped by time period, infinite scroll, and error state.
 *
 * Positioning and overlay behavior are handled by LayoutShell;
 * this component only renders sidebar content.
 */
export default function Sidebar() {
  const { currentSession, setCurrentSession, selectedWorkspace, setSessions } = useChatStore();
  const { sidebarMode, setSidebarOpen } = useUIStore();
  const navigate = useNavigate();

  const workspaceId = selectedWorkspace?.id;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useSessions(workspaceId);

  // Search state with 300ms debounce
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Flatten all pages into a single session list
  const allSessions = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.sessions);
  }, [data]);

  // Filter by search query
  const filteredSessions = useMemo(() => {
    if (!debouncedQuery.trim()) return allSessions;
    const q = debouncedQuery.toLowerCase();
    return allSessions.filter((s) =>
      (s.sessionTitle || 'New Chat').toLowerCase().includes(q),
    );
  }, [allSessions, debouncedQuery]);

  // Group sessions by time period, most recent first within each group
  const groupedSessions = useMemo<SessionGroup[]>(() => {
    const groups = new Map<SessionGroup['label'], ChatSession[]>();
    for (const session of filteredSessions) {
      const label = getSessionGroupLabel(session.lastActivityAt || session.createdAt);
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label)!.push(session);
    }
    // Sort each group by most recent activity first
    for (const sessions of groups.values()) {
      sessions.sort((a, b) => {
        const aTime = new Date(a.lastActivityAt || a.createdAt).getTime();
        const bTime = new Date(b.lastActivityAt || b.createdAt).getTime();
        return bTime - aTime;
      });
    }
    return SESSION_GROUP_ORDER
      .filter((label) => groups.has(label))
      .map((label) => ({ label, sessions: groups.get(label)! }));
  }, [filteredSessions]);

  // Infinite scroll via IntersectionObserver
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // New Chat handler — clear current session for a fresh conversation.
  // The actual backend session is created when the first message is sent.
  const handleNewChat = useCallback(() => {
    if (!workspaceId) return;
    setCurrentSession(null);
    navigate('/chat');
    // Close sidebar on tablet/mobile after action
    if (sidebarMode !== 'persistent') {
      setSidebarOpen(false);
    }
  }, [workspaceId, setCurrentSession, navigate, sidebarMode, setSidebarOpen]);

  // Delete session handler
  const handleDelete = useCallback(
    async (e: React.MouseEvent, sessionId: string) => {
      e.stopPropagation();
      if (deletingIds.has(sessionId)) return;

      setDeletingIds((prev) => new Set(prev).add(sessionId));
      try {
        const response = await chatService.deleteSession(sessionId);
        if (response.success) {
          // If we deleted the active session, clear it
          if (currentSession?.id === sessionId) {
            setCurrentSession(null);
            navigate('/chat');
          }
          // Remove from local sessions list
          setSessions(allSessions.filter((s) => s.id !== sessionId));
          refetch();
          addToast('success', 'Session deleted');
        }
      } catch {
        addToast('error', 'Failed to delete session');
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(sessionId);
          return next;
        });
      }
    },
    [deletingIds, currentSession?.id, setCurrentSession, setSessions, allSessions, refetch],
  );

  // Select session handler — fetch full session with messages from backend
  const handleSelectSession = useCallback(
    async (session: ChatSession) => {
      if (sidebarMode !== 'persistent') {
        setSidebarOpen(false);
      }

      navigate(`/chat/${session.id}`);

      try {
        const response = await chatService.getSession(session.id);
        if (response.success && response.data) {
          // Sort messages chronologically; when timestamps match, user comes before assistant
          if (response.data.messages?.length) {
            const roleOrder: Record<string, number> = { user: 0, assistant: 1, system: 2 };
            response.data.messages.sort((a, b) => {
              const ta = new Date(a.timestamp).getTime();
              const tb = new Date(b.timestamp).getTime();
              if (ta !== tb) return ta - tb;
              return (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9);
            });
          }
          setCurrentSession(response.data);
        } else {
          // Fallback to list data if full fetch fails
          setCurrentSession(session);
        }
      } catch {
        // Fallback to list data if full fetch fails
        setCurrentSession(session);
      }
    },
    [setCurrentSession, sidebarMode, setSidebarOpen, navigate],
  );

  const showCloseButton = sidebarMode !== 'persistent';

  return (
    <aside
      role="navigation"
      className="flex flex-col h-full bg-neutral-50 border-r border-neutral-200"
    >
      {/* Top bar: New Chat + hide sidebar */}
      <div className="flex items-center gap-2 p-3 border-b border-neutral-200">
        {showCloseButton && (
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="p-1.5 rounded-md text-neutral-500 hover:bg-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        <button
          type="button"
          onClick={handleNewChat}
          disabled={!workspaceId}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
            'bg-primary-600 text-white hover:bg-primary-700',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          <Plus className="h-4 w-4" />
          New Chat
        </button>
        {/* Hide sidebar button (desktop persistent mode) */}
        {sidebarMode === 'persistent' && (
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            aria-label="Hide sidebar"
          >
            <PanelLeftClose className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Search input */}
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500 pointer-events-none" />
          <input
            type="text"
            placeholder="Search sessions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              'w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-neutral-300 bg-white',
              'placeholder:text-neutral-500 text-neutral-800',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            )}
            aria-label="Search sessions"
          />
        </div>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto px-1">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600" />
          </div>
        )}

        {/* Error state */}
        {isError && !isLoading && (
          <div className="flex flex-col items-center gap-2 py-8 px-3 text-center">
            <p className="text-sm text-red-600">Failed to load sessions</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2 py-1"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && filteredSessions.length === 0 && (
          <div className="flex flex-col items-center gap-1 py-8 px-3 text-center">
            <MessageSquare className="h-8 w-8 text-neutral-400" />
            <p className="text-sm text-neutral-500">
              {debouncedQuery ? 'No matching sessions' : 'No sessions yet'}
            </p>
          </div>
        )}

        {/* Grouped session list */}
        {!isLoading && !isError && groupedSessions.map((group) => (
          <div key={group.label} className="mb-2">
            <h3 className="px-3 py-1.5 text-xs font-semibold text-neutral-600 uppercase tracking-wider">
              {group.label}
            </h3>
            <ul>
              {group.sessions.map((session) => {
                const isActive = currentSession?.id === session.id;
                const isDeleting = deletingIds.has(session.id);

                return (
                  <li key={session.id}>
                    <button
                      type="button"
                      onClick={() => handleSelectSession(session)}
                      className={cn(
                        'group w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg transition-colors',
                        isActive
                          ? 'bg-neutral-200/70 text-neutral-900 font-medium'
                          : 'hover:bg-neutral-100 text-neutral-600',
                        isDeleting && 'opacity-50 pointer-events-none',
                      )}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <MessageSquare className={cn('h-4 w-4 shrink-0', isActive ? 'text-neutral-700' : 'text-neutral-400')} />
                      <span className="flex-1 truncate">
                        {session.sessionTitle || 'New Chat'}
                      </span>
                      <span
                        className={cn(
                          'shrink-0 p-1 rounded text-neutral-500 hover:text-red-600 hover:bg-red-50 transition-colors',
                          'opacity-0 group-hover:opacity-100 focus-within:opacity-100',
                        )}
                      >
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, session.id)}
                          disabled={isDeleting}
                          className="focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                          aria-label={`Delete session ${session.sessionTitle || 'New Chat'}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-4" aria-hidden="true" />

        {/* Loading more indicator */}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-3">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600" />
          </div>
        )}
      </div>
    </aside>
  );
}
